import { usePricingRights } from '@util/PricingRightsHook';
import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import Tag from '@app/component/tag/Tag';
import CalendarDay from '@app/state/calendar/CalendarDay';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import TableNotHeaderPlaceholder from '@app/component/placeholder/TableNoHeaderPlaceholder';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	useMarketStateSnapshotCalendarKeyboardNavigation,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/hook/useMarketStateSnapshotCalendarKeyboardNavigation';
import {
	useMarketAreaStateForStatDate,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketAreaStateForMonth';
import {
	DisplayMarketAreaStateTag,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/component/DisplayMarketAreaStateTag';
import MarketStateSnapshotDailyAlertNotificationsFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotDailyAlertNotificationsFragment';
import MarketStateSnapshotCalendarDayTableRow
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTableRow';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketStateSnapshotDailyMarketInsightFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotDailyMarketInsightFragment';
import MarketStateSnapshotCalendarDayPriceRecommendationTableRow
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/MarketStateSnapshotCalendarDayPriceRecommendationTableRow';
import AvailabilityDailyStateProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';

export const MarketStateSnapshotCalendarDayTableForMobile: React.FunctionComponent<{
	setSelectedCalendarDay: (calendarDay: CalendarDay | undefined) => any,
	selectedCalendarDay: CalendarDay,
}>
	= ({ setSelectedCalendarDay, selectedCalendarDay }) => {

	const tools = useMarketStateTools();

	const marketStatDateSnapshot = tools.marketStateSnapshot.findDailySnapshot(selectedCalendarDay.display);

	const [competitorSnapshots, setCompetitorSnapshots] = useState<{
		current: MarketStateStatDateCompetitorSnapshot,
		previous: MarketStateStatDateCompetitorSnapshot | undefined
	}[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);

	useEffect(() => {
		if (marketStatDateSnapshot) {
			setIsLoading(true);
			marketStatDateSnapshot.getCompetitorSnapshotsComparison(tools.findPreviousSnapshot)
				.then(snapshots => {
					setCompetitorSnapshots(snapshots);
					setIsLoading(false);
				})
				.catch(error => {
					setIsLoading(false);
				});
		}
	}, [marketStatDateSnapshot, tools.findPreviousSnapshot]);

	if (!marketStatDateSnapshot) return null;

	const calendarNavigation = useMarketStateSnapshotCalendarKeyboardNavigation();

	const marketAreaStateComparisonOutput = useMarketAreaStateForStatDate(selectedCalendarDay?.display);
	const currentMarketAreaState = marketAreaStateComparisonOutput?.current;

	let recommendationIndex = marketStatDateSnapshot?.findRecommendationIndex();
	const showPricingAction = canViewRecommendations || canChangePrices;

	return <div className="mt-3 py-4 px-4 sm:px-6 bg-white shadow rounded-lg">
		<div className="mt-3 mb-1">
			<div className="relative flex rounded-md bg-white items-stretch justify-center">
				<button
					type="button"
					onClick={() => calendarNavigation.previousDay(selectedCalendarDay, setSelectedCalendarDay, tools.calendarMonth)}
					className="flex h-8 w-12 items-center justify-center pr-1 text-gray-400 hover:text-gray-500 focus:relative hover:bg-gray-50"
					title="Previous day"
				>
					<span className="sr-only">Previous day</span>
					<ChevronLeftIcon className="h-5 w-5" />
				</button>
				<button type="button"
				        onClick={() => calendarNavigation.today(setSelectedCalendarDay, tools.calendarMonth)}
				        className="min-w-[150px] px-3.5 h-8 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative w-auto">
					{Utilities.formatDateString(selectedCalendarDay.display, 'ddd D MMM \'YY', false)}
				</button>
				<button
					type="button"
					onClick={() => calendarNavigation.nextDay(selectedCalendarDay, setSelectedCalendarDay, tools.calendarMonth)}
					className="flex h-8 w-12 items-center justify-center rounded-r-md pl-1 text-gray-400 hover:text-gray-500 focus:relative hover:bg-gray-50"
					title="Next day"
				>
					<span className="sr-only">Next day</span>
					<ChevronRightIcon className="h-5 w-5" />
				</button>
			</div>
		</div>
		<div className="mt-5">
			<div className="relative">
				<div className="absolute inset-0 flex items-center">
					<div className="w-full border-t border-gray-300"></div>
				</div>
				<div className="relative flex items-center justify-between"><span
					className="bg-white pr-3 text-sm font-semibold leading-6 text-gray-800">{companyHotel.comparisonLabel()} Compset Rate</span>
				</div>
			</div>
			<div className="relative">
				<div className="flex flex-wrap justify-between"><p
					className="text-2xl font-medium tracking-tight text-gray-900">{!!marketStatDateSnapshot.current.comparisonPrice(companyHotel) ? Utilities.formatPrice(marketStatDateSnapshot.current.comparisonPrice(companyHotel)!) : companyHotel.usesWeightedAverage() ? 'N/A' : '-'}</p>
					<div className="relative flex flex-wrap items-center gap-x-1">
						<span className="shrink-0 w-1.5 h-1.5 bg-gray-200 rounded-full mr-1"></span>
						<span className="text-xs font-light text-gray-400">Your difference</span>
						{marketStatDateSnapshot.current.evaluatePriceMedian(tools.companyHotel).map((result, idx) =>
							<Tag key={`index_${idx}`} state={result.state}>{result.value}</Tag>)}
						{currentMarketAreaState && !companyHotel.hasMarketInsightModule() && <>
							<span className="shrink-0 w-1.5 h-1.5 bg-gray-200 mr-1 rounded-full"></span>
							<span className="text-xs font-normal text-gray-400">Market demand</span>
							<DisplayMarketAreaStateTag output={marketAreaStateComparisonOutput} />
						</>}
					</div>
				</div>
				<span
					className="text-xs text-gray-300 italic">• Updated {Utilities.formatDateString(marketStatDateSnapshot.current.latestUpdateDate!, 'DD MMMM HH:mm')}</span>
			</div>
		</div>
		<div className="mb-4">
			{currentMarketAreaState && companyHotel.hasMarketInsightModule() &&
				<MarketStateSnapshotDailyMarketInsightFragment calendarDay={selectedCalendarDay}
				                                               marketAreaStateComparison={marketAreaStateComparisonOutput} />}
		</div>
		<div className="my-2">
			<MarketStateSnapshotDailyAlertNotificationsFragment calendarDay={selectedCalendarDay} />
		</div>

		<div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 rounded-md">
			<div className="inline-block min-w-full align-middle max-h-[500px]">
				<table className="min-w-full divide-y divide-gray-300">
					<thead className="bg-gray-50 sticky-table-header-only">
					<tr>
						<th scope="col"
						    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
							Hotel
						</th>
						<th scope="col"
						    className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
							Price
						</th>
						<th scope="col" colSpan={2}
						    className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
						</th>
						<th scope="col"
						    className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
							Availability
						</th>
						<th scope="col"
						    className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
							Type
						</th>
						<th scope="col"
						    className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900" />
						{showPricingAction && <th scope="col"
						                                   className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900" />}
					</tr>
					</thead>
					<tbody className="divide-y divide-gray-200 bg-white">
					<AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={marketStatDateSnapshot.current.statDate}>
						{isLoading ? (
							<tr>
								<td colSpan={showPricingAction ? 8 : 7} className="text-center">
									<TableNotHeaderPlaceholder />
								</td>
							</tr>
						) : competitorSnapshots.map((item, idx) => {

								const showRecommendation = !selectedCalendarDay.isPast && recommendationIndex !== undefined
									&& recommendationIndex !== null
									&& recommendationIndex === idx && (canViewRecommendations && companyHotel.hasRecommendationNormalEngineRoompulseOrBraintree())
									&& !marketStatDateSnapshot.priceRecommendationStateSnapshot?.isSubmitted
									&& marketStatDateSnapshot.priceRecommendationStateSnapshot?.hasValidRecommendation() === true;

								return <React.Fragment key={`mobile_daily_${idx}`}>
									{showRecommendation && <MarketStateSnapshotCalendarDayPriceRecommendationTableRow
										marketStateForStatDate={marketStatDateSnapshot} />}
									<MarketStateSnapshotCalendarDayTableRow
										calendarDay={selectedCalendarDay}
										currentMarketStateForDay={marketStatDateSnapshot!.current}
										priceRecommendationState={marketStatDateSnapshot.priceRecommendationStateSnapshot}
										current={item.current}
										previous={item.previous}
										key={`mobile_daily_${idx}`} />

								</React.Fragment>;
							},
						)}
						{!isLoading && !selectedCalendarDay.isPast && recommendationIndex === competitorSnapshots.length
							&& canViewRecommendations && companyHotel.hasRecommendationNormalEngineRoompulseOrBraintree()
							&& !marketStatDateSnapshot.priceRecommendationStateSnapshot?.isSubmitted
							&& marketStatDateSnapshot.priceRecommendationStateSnapshot?.hasValidRecommendation() === true &&
							<MarketStateSnapshotCalendarDayPriceRecommendationTableRow marketStateForStatDate={marketStatDateSnapshot} />}
					</AvailabilityDailyStateProvider>
					</tbody>
				</table>
			</div>
		</div>
	</div>;
};
